import { FC, useContext, useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, parse } from "date-fns";
import ReactInputMask from "react-input-mask";
import DatePicker from "react-datepicker";
import Select, { SingleValue } from "react-select";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import { EnrollNurseSchema } from "../../validation/enrollNurse";
import { US_STATES } from "src/constants";
import {
    AddQNSpecialistResponse,
    DobFieldProps,
    EnrollNurseFormValues,
    FieldRowProps,
    SelectFieldProps,
    TextFieldProps,
} from "./types";

const DEFAULT_VALUES: EnrollNurseFormValues = {
    firstname: "",
    lastname: "",
    email: "",
    dob: "",
    gender: "",
    street: "",
    suite: "",
    city: "",
    state: "",
    zipcode: "",
    phone_no: "",
    phone_type: "Cell",
    prescribing_doctor: "",
    consent: false,
};


const FieldRow: FC<FieldRowProps> = ({ label, width = "half", error, children }) => {
    const wrapperCol = width === "full" ? "col-lg-12" : "col-lg-6";
    const labelCol = width === "full" ? "col-lg-2" : "col-lg-4";
    const inputCol = width === "full" ? "col-lg-10" : "col-lg-8";

    return (
        <div className={`${wrapperCol} mb-3`}>
            <div className="row">
                <label htmlFor={`${label.toLowerCase()}_field`} className={`form-label fw-bold ${labelCol} col-md-4 col-12`}>{label}</label>
                <div className={`${inputCol} col-md-8 col-12`}>
                    {children}
                    {error ? <div className="text-danger text-small mt-2">{error}</div> : null}
                </div>
            </div>
        </div>
    );
};

const TextField = <T extends FieldValues>({
    name, control, label, placeholder, type = "text", width = "half", showError = true,
}: TextFieldProps<T>) => (
    <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
            <FieldRow label={label} width={width} error={showError ? error?.message : undefined}>
                <input
                    type={type}
                    className={`form-control ${error && showError ? "is-invalid" : ""}`}
                    placeholder={placeholder}
                    {...field}
                />
            </FieldRow>
        )}
    />
);

const SelectField = <T extends FieldValues>({
  name, control, label, options, width = "half",
}: SelectFieldProps<T>) => {
  const inputId = `${String(name)}_field`;
  const getValue = (value?: string) => options.find((option) => option.value === value) ?? null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FieldRow label={label} width={width} error={error?.message}>
          <Select<{ value: string; label: string }>
            inputId={inputId}
            name={field.name}
            options={options}
            classNames={{
              control(_props) {
                return `input-group ${error && error.message ? "border-danger" : ""}`
              },
            }}
            noOptionsMessage={() => `Select...`}
            value={getValue(field.value)}
            onChange={(value: SingleValue<{ value: string; label: string }>) => field.onChange(value?.value ?? "")}
            onBlur={field.onBlur}
            placeholder={label}
            menuShouldScrollIntoView={false}
            menuPosition="fixed"
          />
        </FieldRow>
      )}
    />
  )
};

const DobField: FC<DobFieldProps> = ({ control }) => {
  const [open, setOpen] = useState(false);

  return (
        <Controller
            name="dob"
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FieldRow label="DOB *" error={error?.message}>
                    <div className="position-relative">
                        <DatePicker
                            selected={field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                            onChange={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                            dateFormat="MM/dd/yyyy"
                            className={`form-control ${error ? "is-invalid" : ""}`}
                            wrapperClassName="w-100"
                            placeholderText="mm/dd/yyyy"
                            customInput={
                                <ReactInputMask mask="99/99/9999">
                                    {(inputProps: {}) => <input {...inputProps} />}
                                </ReactInputMask>
                            }
                            maxDate={new Date()}
                            open={open}
                            onFocus={() => setOpen(true)}
                            onClickOutside={() => setOpen(false)}
                            onSelect={() => setOpen(false)}
                            onKeyDown={(e) => {
                                if (e.key === "Tab" || e.key === "Escape") setOpen(false);
                            }}
                            showMonthDropdown
                            showYearDropdown
                        />
                        <i
                            className="bi bi-calendar position-absolute"
                            style={{
                                right: error ? "32px" : "15px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                            }}
                        />
                    </div>
                </FieldRow>
            )}
        />
    );
};


const isSuccessResponse = (data: AddQNSpecialistResponse): boolean => {
    const s = data.status;

    if (s && typeof s === "object") {
        const code = Number(s.code);
        if (Number.isFinite(code)) return code >= 200 && code < 300;
    }

    if (typeof s === "number") return s >= 200 && s < 300;

    if (typeof s === "string") {
        const numeric = Number(s);
        if (Number.isFinite(numeric)) return numeric >= 200 && numeric < 300;
        return ["success", "ok", "created"].includes(s.toLowerCase());
    }

    return true;
};

const extractMessage = (data: AddQNSpecialistResponse, fallback: string): string => {
    if (data.status && typeof data.status === "object" && data.status.message) return data.status.message;
    if (data.message) return data.message;
    return fallback;
};


const EnrollNurse: FC = (): JSX.Element => {
  const { showToast } = useContext(ToastContext);
  const { control, handleSubmit, reset } = useForm<EnrollNurseFormValues>({
    mode: "all",
    resolver: yupResolver(EnrollNurseSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { loading, data, error, mutate, removeErrorAndData } = useFetch<AddQNSpecialistResponse>(
    APIEndpoints.ADD_QN_SPECIALIST,
  );

  useEffect(() => {
    if (data) {
      const success = isSuccessResponse(data);
      const message = extractMessage(
        data,
        success ? "Enrollment submitted successfully." : "Something went wrong. Please try again.",
      );

      showToast([message], success ? ToastTypes.SUCCESS : ToastTypes.ERROR);

      if (success) reset(DEFAULT_VALUES);

      removeErrorAndData();
    }
  }, [data, error, reset, showToast, removeErrorAndData]);

  const submitForm = (values: EnrollNurseFormValues) => {
    if (error || data) removeErrorAndData();

    const { consent, ...payload } = values;
    mutate({ qn_specialist: payload });
  };

  return (
    <div className="enrollment-new d-flex flex-fill flex-column justify-content-between overflow-auto m-0">
      <div className="container">
        <div className="container container-short mt-0">
          {/* <h1 className="text-primary mb-5">Qutenza Nurse Specialist Enrollment Form</h1> */}
          {/* <h1 className="text-primary">My QUTENZA Connect Practice Registration</h1>
          <p>Fill out the form to register your practice in the My QUTENZA Connect Savings Program.</p>
          <p>
            To secure your privacy, this session will time out if left idle for 15 minutes.
            Information will not be saved and you will need to restart registration.
          </p> */}
          {/*<div style={{ height: 61 }}></div>*/}

          <div className="card mb-4 rounded-1">
            <div className="card-header">
              <h3 className="text-white text-uppercase fs-4 mb-0 text-start fw-normal">
                Qutenza Nurse Specialist Enrollment Form
              </h3>
            </div>

            <div className="card-body">
              <form onSubmit={handleSubmit(submitForm)}>
                <div className="row">
                  <TextField name="firstname" control={control} label="First Name *" placeholder="First Name" />
                  <TextField name="lastname" control={control} label="Last Name *" placeholder="Last Name" />
                </div>

                <div className="row">
                  <TextField name="email" control={control} label="Email *" placeholder="Email" type="email" width="full" />
                </div>

                <div className="row">
                  <DobField control={control} />
                  <SelectField
                    name="gender"
                    control={control}
                    label="Gender *"
                    options={[
                      // { value: "", label: "Select Gender" },
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                      { value: "Other", label: "Other" },
                    ]}
                  />
                </div>

                <div className="row">
                  <TextField name="street" control={control} label="Street" placeholder="Street Address" />
                  <TextField name="suite" control={control} label="Suite" placeholder="Suite Address" showError={false} />
                </div>

                <div className="row">
                  <TextField name="city" control={control} label="City" placeholder="City" />
                  <SelectField
                    name="state"
                    control={control}
                    label="State"
                    options={[
                      // { value: "", label: "Select State" },
                      ...US_STATES,
                    ]}
                  />
                </div>

                <div className="row">
                  <TextField name="zipcode" control={control} label="Zip" placeholder="Zip Code" width="full" />
                </div>

                <div className="row">
                  <div className="col-md-6 col-12 col-lg-6">
                    <Controller
                      name="phone_no"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div className="row mb-3">
                          <label className="form-label fw-bold col-md-4 col-12">Phone Number *</label>
                          <div className="col-md-8 col-12">
                            <ReactInputMask
                              {...field}
                              mask="(999) 999-9999"
                              onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                            >
                              {(inputProps: {}) => 
                                <input 
                                  type="text" id="phone_no"
                                  className={`form-control ${error && error.message ? 'is-invalid' : ''}`}
                                  placeholder="(555) 555-5555" {...inputProps}
                                />
                              }
                            </ReactInputMask>

                            {error && error.message ? (
                              <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2"></div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <SelectField
                    name="phone_type"
                    control={control}
                    label="Phone Type"
                    options={[
                      { value: "Cell", label: "Cell" },
                      { value: "Home", label: "Home" },
                      { value: "Work", label: "Work" },
                    ]}
                  />
                </div>

                <div className="row">
                  <TextField
                    name="prescribing_doctor"
                    control={control}
                    label="QUTENZA Prescribing Doctor *"
                    placeholder="Name"
                    width="full"
                  />
                </div>

                  <div className="row mb-3">
                    <label className="form-label fw-bold col-lg-2 col-md-4 col-12" />
                    <div className="col-lg-10 col-md-8 col-12">
                      <div className="form-check">
                        <Controller
                          name="consent"
                          control={control}
                          defaultValue={false}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <input
                                className={`${error && error.message ? "is-invalid" : ""} form-check-input`}
                                type="checkbox"
                                id="nurse-enroll-consent"
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                              <label className="form-check-label text-black" htmlFor="nurse-enroll-consent">
                                I confirm that I have been prescribed QUTENZA® by my healthcare provider and agree to be
                                contacted by a QUTENZA Nurse Specialist for education and support related to my treatment.
                                I understand that I may be contacted by phone, email, or text message using the information
                                I provide. Message and data rates may apply. Participation in the program is voluntary, and
                                I may opt out at any time.
                              </label>

                              {error && error.message && <div id="nurse-enroll-consent" className="invalid-feedback mt-2">{error.message}</div>}
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                <div className="row">
                  <label className="form-label fw-bold col-lg-2 col-md-4 col-12" />
                  <div className="col-lg-10 col-md-8 col-12">
                    <button
                      disabled={loading}
                      className="btn btn-primary"
                      type="submit"
                    >
                      {loading ? (<i className="spinner-border me-2"></i>) : null}
                      Enroll
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollNurse;
